import { List, Button, Tag, Modal, Spin, Tooltip } from "antd";
import { useState } from "react";
import { EditFilled, DeleteFilled, LockFilled, UnlockFilled } from "@ant-design/icons";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import { EtapeService } from "../../services/EtapeService";
import EtapeEditForm from "./EtapeEditForm";
import PropTypes from "prop-types";
import styles from "./EtapeList.module.scss";

const EtapeList = ({ paginationData, setPaginationData, etapes, setEtapes, searchParams }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedEtape, setSelectedEtape] = useState(null);

  const loadMore = () => {
    if (paginationData?.next_page_url && !loadingMore) {
      setLoadingMore(true);
      EtapeService.post(paginationData.next_page_url, searchParams)
        .then((response) => {
          if (!response) return;
          setPaginationData(response.data);
          setEtapes((prevEtapes) => [...prevEtapes, ...response.data.data]);
        })
        .catch(catchError)
        .finally(() => setLoadingMore(false));
    }
  };

  const handleDelete = (idOrUid) => {
    setLoading(true);
    EtapeService.getInstance()
      .remove(idOrUid)
      .then((response) => {
        Toast.success(response.message);
        setEtapes((items) => items.filter((item) => item.uid !== idOrUid));
      })
      .catch(catchError)
      .finally(() => setLoading(false));
  };

  const openEditModal = (item) => {
    setSelectedEtape(item);
    setUpdateModalVisible(true);
  };

  const handleEnable = (etape) => {
    EtapeService.getInstance()
      .enable(etape.uid)
      .then((response) => {
        Toast.success(response.message);
        setEtapes((prevEtapes) =>
          prevEtapes.map((u) => (u.uid === response.data.uid ? response.data : u))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  const handleDisable = (etape) => {
    EtapeService.getInstance()
      .disable(etape.uid)
      .then((response) => {
        Toast.success(response.message);
        setEtapes((prevEtapes) =>
          prevEtapes.map((u) => (u.uid === response.data.uid ? response.data : u))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  const handleUpdate = (item) => {
    setEtapes((prevEtapes) => prevEtapes.map((u) => (u.uid === item.uid ? item : u)));
    setSelectedEtape(null);
    setUpdateModalVisible(false);
  };

  return (
    <>
      <Spin spinning={loading}>
        <List
          className={styles.etapeList}
          itemLayout="horizontal"
          dataSource={etapes}
          renderItem={(item) => (
            <List.Item
              className={styles.listItem}
              actions={[
                <Tooltip title="Modifier" key="edit">
                  <Button icon={<EditFilled />} onClick={() => openEditModal(item)} type="text" />
                </Tooltip>,
                <Tooltip title={item.enabled ? "Désactiver" : "Activer"} key="toggle">
                  <Button
                    icon={item.enabled ? <LockFilled /> : <UnlockFilled />}
                    onClick={() => {
                      Modal.confirm({
                        title: `Voulez-vous ${item.enabled ? "désactiver" : "activer"} cette étape ?`,
                        okText: item.enabled ? "Désactiver" : "Activer",
                        cancelText: "Annuler",
                        okType: item.enabled ? "danger" : "primary",
                        onOk: () => (item.enabled ? handleDisable(item) : handleEnable(item)),
                      });
                    }}
                    type="text"
                  />
                </Tooltip>,
                <Tooltip title="Supprimer" key="delete">
                  <Button
                    icon={<DeleteFilled />}
                    onClick={() =>
                      Modal.confirm({
                        title: "Voulez-vous vraiment supprimer cette étape ?",
                        okText: "Confirmer",
                        cancelText: "Annuler",
                        okType: "danger",
                        onOk: () => handleDelete(item.uid),
                      })
                    }
                    type="text"
                    danger
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={<span className={styles.etapeTitle}>{item.nom}</span>}
                description={
                  <div className={styles.metaDescription}>
                    <Tag color={item.enabled ? "success" : "error"}>
                      {item.enabled ? "Actif" : "Inactif"}
                    </Tag>
                    <Tooltip title={item.description}>
                      <span className={styles.description}>
                        {item.description && item.description.length > 50
                          ? `${item.description.substring(0, 50)}...`
                          : item.description || "N/A"}
                      </span>
                    </Tooltip>
                  </div>
                }
              />
            </List.Item>
          )}
        />
        {paginationData?.next_page_url && (
          <div className={styles.loadMore}>
            <Button onClick={loadMore} loading={loadingMore}>
              Charger plus
            </Button>
          </div>
        )}
      </Spin>

      {selectedEtape && (
        <EtapeEditForm
          visible={updateModalVisible}
          uid={selectedEtape.uid}
          onUpdate={handleUpdate}
          onCancel={() => setUpdateModalVisible(false)}
        />
      )}
    </>
  );
};

export default EtapeList;

EtapeList.propTypes = {
  paginationData: PropTypes.object.isRequired,
  searchParams: PropTypes.object,
  setPaginationData: PropTypes.func.isRequired,
  etapes: PropTypes.array.isRequired,
  setEtapes: PropTypes.func.isRequired,
};
